import React, { useState } from "react";
import { formatDateForInput } from "../../../../utils/formatters";
import {
  getHtmlDateMinMax,
  normalizeToYmd,
  validateDobYmd,
  validatePastOnlyYmd,
} from "../../../../utils/dateFieldConstraints";
import DatePickerInput from "../DatePickerInput";
import StateCitySelect from "../../../StateCitySelect";
import ExistingAddressPicker from "../../../ExistingAddressPicker";
import { resolveStateCode } from "../../../../utils/stateUtils";
import {
  GENDER_OPTIONS,
  RELATIONSHIP_OPTIONS,
} from "../../../../constants/membersConstants";
import { COUNTRY_OPTIONS } from "../../../../constants/projectConstants";

import { formatSSNInput, validateSSNDigits, validateEmail } from "../../../../utils/validationHelpers";
import {
  getBrokerageFlags,
  showMaritalStatus,
} from "../../../../utils/brokerageFields";
import { isDeceasedOrDecedent, isCustodialMinorOwner } from "../../../../utils/memberLinks";
import { getWaivedMemberFields } from "../../../../utils/memberAccountRoles";

const TITLE_OPTIONS = ["Mr", "Ms", "Mrs"];
const MARITAL_OPTIONS = ["Married", "Single", "Divorced", "Widowed"];
const PersonalDetailsForm = ({
  householdData,
  fieldData,
  missingFields,
  entityId,
  section,
  onSave,
  onSaveAndExit,
  saving,
  nextLabel,
}) => {
  const member = fieldData || {};
  const personalInfo = member.personal_info || {};
  const isPrimary = member.is_primary === true;
  const brokerageFlags = getBrokerageFlags(householdData);
  const isOwner = (() => {
    const accounts = householdData?.accounts;
    if (!Array.isArray(accounts) || !entityId) return false;
    return accounts.some((account) => {
      const info = account?.owners_info || account?.owners;
      if (!Array.isArray(info)) return false;
      return info.some(
        (owner) =>
          owner &&
          (String(owner.id) === String(entityId) ||
            String(owner.member_id) === String(entityId)),
      );
    });
  })();

  if (section === "address") {
    return (
      <AddressSection
        member={member}
        entityId={entityId}
        household={householdData}
        onSave={onSave}
        onSaveAndExit={onSaveAndExit}
        saving={saving}
        nextLabel={nextLabel}
      />
    );
  }

  return (
    <PersonalSection
      member={member}
      personalInfo={personalInfo}
      isPrimary={isPrimary}
      dobIsPrimary={isPrimary && !isCustodialMinorOwner(householdData?.accounts, entityId)}
      isOwner={isOwner}
      waivedFields={getWaivedMemberFields(householdData, entityId, brokerageFlags)}
      showMarital={showMaritalStatus(brokerageFlags)}
      deceasedOnly={isDeceasedOrDecedent(member, householdData?.accounts)}
      entityId={entityId}
      missingFields={missingFields}
      onSave={onSave}
      onSaveAndExit={onSaveAndExit}
      saving={saving}
      nextLabel={nextLabel}
    />
  );
};

const PersonalSection = ({
  member,
  personalInfo,
  isPrimary,
  dobIsPrimary,
  isOwner,
  waivedFields,
  showMarital,
  deceasedOnly,
  entityId,
  missingFields,
  onSave,
  onSaveAndExit,
  saving,
  nextLabel,
}) => {
  const missingKeys = Array.isArray(missingFields) ? missingFields : [];
  const mustShowDeceased = missingKeys.includes("is_deceased");
  const mustShowDateOfDeath = missingKeys.includes("date_of_death");
  const waived = waivedFields || new Set();

  const PERSONAL_FIELDS = deceasedOnly
    ? [
        { key: "first_name", label: "First Name", type: "text" },
        { key: "last_name", label: "Last Name", type: "text" },
        { key: "ssn_or_tin", label: "SSN or TIN", type: "ssn" },
        { key: "dob", label: "Date of Birth", type: "date" },
        { key: "is_deceased", label: "Deceased", type: "checkbox" },
        { key: "date_of_death", label: "Date of Death", type: "date" },
      ]
    : [
        { key: "title", label: "Title", type: "select", options: TITLE_OPTIONS },
        { key: "gender", label: "Gender", type: "select", options: GENDER_OPTIONS },
        { key: "first_name", label: "First Name", type: "text" },
        { key: "last_name", label: "Last Name", type: "text" },
        ...(showMarital
          ? [{ key: "marital_status", label: "Marital Status", type: "select", options: MARITAL_OPTIONS }]
          : []),
        ...(!isPrimary
          ? [{ key: "relationship", label: "Relationship to Primary", type: "select", options: RELATIONSHIP_OPTIONS }]
          : []),
        { key: "ssn_or_tin", label: "SSN or TIN", type: "ssn" },
        { key: "dob", label: "Date of Birth", type: "date" },
        { key: "email", label: "Email", type: "email" },
        ...(isOwner ? [{ key: "username", label: "Username", type: "text" }] : []),
        { key: "country_of_citizenship", label: "Country of Citizenship", type: "select", options: COUNTRY_OPTIONS },
        { key: "is_deceased", label: "Deceased", type: "checkbox" },
        { key: "date_of_death", label: "Date of Death", type: "date" },
      ].filter((field) => !waived.has(field.key));

  const [formData, setFormData] = useState(() => {
    const initial = {};
    PERSONAL_FIELDS.forEach((f) => {
      if (f.key === "dob" || f.key === "date_of_death") {
        initial[f.key] = personalInfo[f.key] ? formatDateForInput(personalInfo[f.key]) : "";
      } else if (f.key === "ssn_or_tin") {
        initial[f.key] = personalInfo.ssn_or_tin || "";
        initial.ssn_display = personalInfo.ssn_or_tin ? formatSSNInput(personalInfo.ssn_or_tin) : "";
      } else if (f.key === "country_of_citizenship") {
        const stored = personalInfo[f.key];
        initial[f.key] = !stored || stored === "USA" ? "United States" : stored;
      } else if (f.key === "is_deceased") {
        initial[f.key] = personalInfo[f.key] || false;
      } else {
        initial[f.key] = personalInfo[f.key] || "";
      }
    });
    return initial;
  });

  const fieldsToShow = React.useMemo(() => {
    return PERSONAL_FIELDS.filter((f) => {
      if (f.key === "is_deceased") {
        if (mustShowDeceased) return true;
      }
      if (f.key === "date_of_death") {
        const deceased = formData?.is_deceased;
        if (!deceased && !deceasedOnly) return false;
        if (mustShowDateOfDeath) return true;
      }
      const val = personalInfo[f.key];
      return val === null || val === undefined || val === "";
    });
  }, [formData.is_deceased, personalInfo, mustShowDeceased, mustShowDateOfDeath, deceasedOnly]);

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleSSNChange = (value) => {
    const digits = value.replace(/[^\d*]/g, "").substring(0, 9);
    setFormData((prev) => ({
      ...prev,
      ssn_or_tin: digits,
      ssn_display: formatSSNInput(digits),
    }));
    setErrors((prev) => ({ ...prev, ssn_or_tin: null }));
  };

  const buildPayload = () => {
    const update = {};

    fieldsToShow.forEach((f) => {
      if (f.key === "ssn_or_tin") {
        const digits = formData.ssn_or_tin;
        if (digits && digits.length === 9) {
          update.ssn_or_tin = digits;
        }
      } else if (f.key === "country_of_citizenship") {
        const val = String(formData[f.key] || "").trim();
        update[f.key] = val === "" || val === "United States" ? "USA" : val;
      } else {
        const val = formData[f.key];
        const str = typeof val === "string" ? val.trim() : val;
        if (str !== undefined && str !== null && str !== "") {
          update[f.key] = typeof val === "string" ? str : val;
        }
      }
    });

    return { household_members: { [entityId]: { personal_info: update } } };
  };

  const dobBounds = getHtmlDateMinMax(
    dobIsPrimary ? "dob_primary" : "dob_non_primary",
  );
  const dateOfDeathBounds = getHtmlDateMinMax("past_only");

  const handleSubmit = (saveCallback) => {
    if (fieldsToShow.some((f) => f.key === "ssn_or_tin") && formData.ssn_or_tin) {
      const digits = formData.ssn_or_tin;
      if (digits.length !== 9) {
        setErrors((prev) => ({ ...prev, ssn_or_tin: "SSN/TIN must be 9 digits" }));
        return;
      }
      if (!validateSSNDigits(digits)) {
        setErrors((prev) => ({ ...prev, ssn_or_tin: "Invalid SSN/TIN" }));
        return;
      }
    }
    const emailValue = (formData.email || "").trim();
    if (fieldsToShow.some((f) => f.key === "email") && emailValue) {
      if (!validateEmail(emailValue)) {
        setErrors((prev) => ({ ...prev, email: "Please enter a valid email address" }));
        return;
      }
    }
    if (fieldsToShow.some((f) => f.key === "dob") && formData.dob) {
      const dobErr = validateDobYmd(normalizeToYmd(formData.dob), dobIsPrimary);
      if (dobErr) {
        setErrors((prev) => ({ ...prev, dob: dobErr }));
        return;
      }
    }
    if (fieldsToShow.some((f) => f.key === "date_of_death") && formData.date_of_death) {
      const dodErr = validatePastOnlyYmd(
        normalizeToYmd(formData.date_of_death),
        "Date of death",
      );
      if (dodErr) {
        setErrors((prev) => ({ ...prev, date_of_death: dodErr }));
        return;
      }
    }
    saveCallback(buildPayload());
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        <span className="text-sm text-red-500 font-medium">
          {fieldsToShow.length} Required field{fieldsToShow.length !== 1 ? "s" : ""} missing
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {fieldsToShow.map((f) => (
          <div key={f.key}>
            {f.type !== "checkbox" && (
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {f.label} <span className="text-red-500">*</span>
              </label>
            )}
            {f.type === "select" ? (
              <select
                value={formData[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors[f.key] ? "border-red-400" : "border-gray-300"} focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
              >
                <option value="">Select...</option>
                {f.options.map((opt) => {
                  const value = typeof opt === "object" ? opt.value : opt;
                  const label = typeof opt === "object" ? opt.label : opt;
                  return (
                    <option key={value} value={value}>{label}</option>
                  );
                })}
              </select>
            ) : f.type === "ssn" ? (
              <input
                type="text"
                value={formData.ssn_display || ""}
                onFocus={(e) => {
                  if (String(formData.ssn_display || "").includes("*")) {
                    setFormData((prev) => ({
                      ...prev,
                      ssn_or_tin: "",
                      ssn_display: "",
                    }));
                  }
                }}
                onChange={(e) => handleSSNChange(e.target.value)}
                onBlur={(e) => {
                  if (formData.ssn_or_tin === "" && String(personalInfo.ssn_or_tin || "").includes("*")) {
                    const originalMasked = String(personalInfo.ssn_or_tin);
                    setFormData((prev) => ({
                      ...prev,
                      ssn_or_tin: originalMasked,
                      ssn_display: formatSSNInput(originalMasked),
                    }));
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.ssn_or_tin ? "border-red-400" : "border-gray-300"} focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
                placeholder="XXX-XX-XXXX"
                maxLength={11}
              />
            ) : f.type === "date" ? (
              <DatePickerInput
                value={formData[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors[f.key] ? "border-red-400" : "border-gray-300"} focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
                min={
                  f.key === "dob"
                    ? dobBounds.min
                    : f.key === "date_of_death"
                      ? dateOfDeathBounds.min
                      : undefined
                }
                max={
                  f.key === "dob"
                    ? dobBounds.max
                    : f.key === "date_of_death"
                      ? dateOfDeathBounds.max
                      : undefined
                }
              />
            ) : f.type === "checkbox" ? (
              <div className="flex items-center min-h-[38px] md:h-full">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!formData[f.key]}
                    onChange={(e) => handleChange(f.key, e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm font-medium text-gray-700">{f.label}</span>
                </label>
              </div>
            ) : (
              <input
                type={f.type}
                value={formData[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors[f.key] ? "border-red-400" : "border-gray-300"} focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
              />
            )}
            {errors[f.key] && <p className="text-xs text-red-500 mt-1">{errors[f.key]}</p>}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button onClick={() => handleSubmit(onSaveAndExit)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">
          Save & Exit
        </button>
        <button onClick={() => handleSubmit(onSave)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2">
          {saving && <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

const AddressSection = ({ member, entityId, household, onSave, onSaveAndExit, saving, nextLabel }) => {
  const homeAddr = (member.personal_info?.address || []).find((a) => a.type === "home") || {};

  const ADDRESS_FIELDS = [
    { key: "address_line_1", label: "Address Line 1" },
    { key: "address_line_2", label: "Address Line 2", optional: true },
    { key: "city", label: "City" },
    { key: "province", label: "Province/State" },
    { key: "postal_code", label: "Postal Code" },
    { key: "country", label: "Country" },
    { key: "phone", label: "Phone" },
  ];

  const formatPhoneInput = (raw) => {
    const d = (raw || "").replace(/\D/g, "").substring(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  const [formData, setFormData] = useState(() => {
    const initial = {};
    ADDRESS_FIELDS.forEach((f) => {
      const val = homeAddr[f.key] || (f.key === "country" ? "USA" : "");
      initial[f.key] = f.key === "phone" ? formatPhoneInput(val) : val;
    });
    return initial;
  });

  const [errors, setErrors] = useState({});
  const [addrPickerKey, setAddrPickerKey] = useState(0);
  const addressLineFields = ADDRESS_FIELDS.filter(
    (f) => f.key === "address_line_1" || f.key === "address_line_2",
  );
  const bottomFields = ADDRESS_FIELDS.filter(
    (f) => f.key === "postal_code" || f.key === "phone",
  );

  const handleChange = (field, value) => {
    const val = field === "phone" ? formatPhoneInput(value) : value;
    setFormData((prev) => ({ ...prev, [field]: val }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const buildPayload = () => {
    const addr = { type: "home" };

    ADDRESS_FIELDS.forEach((f) => {
      const raw = formData[f.key];
      let val = typeof raw === "string" ? raw.trim() : raw;
      if (f.key === "phone" && val) val = val.replace(/\D/g, "");
      if (f.key === "province" && val) val = resolveStateCode(val);
      if (val) {
        addr[f.key] = val;
      }
    });

    const existingAddresses = (member.personal_info?.address || []).filter((a) => a.type !== "home");

    return {
      household_members: {
        [entityId]: {
          personal_info: {
            address: [...existingAddresses, addr],
          },
        },
      },
    };
  };

  const handleSubmit = (saveCallback) => {
    saveCallback(buildPayload());
  };

  const missingCount = ADDRESS_FIELDS.filter((f) => !f.optional && !homeAddr[f.key]).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        <span className="text-sm text-red-500 font-medium">
          {missingCount} Required field{missingCount !== 1 ? "s" : ""} missing
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        <div className="md:col-span-2">
          <ExistingAddressPicker
            household={household}
            excludeMemberId={entityId}
            getField={(k) => formData[k]}
            setField={(k, v) => handleChange(k, v)}
            onAfterSelect={() => setAddrPickerKey((k) => k + 1)}
          />
        </div>
        {addressLineFields.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {f.label} {!f.optional && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={formData[f.key]}
              onChange={(e) => handleChange(f.key, e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg text-sm ${errors[f.key] ? "border-red-400" : "border-gray-300"} focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
            />
            {errors[f.key] && <p className="text-xs text-red-500 mt-1">{errors[f.key]}</p>}
          </div>
        ))}
        <div className="md:col-span-2 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
          <StateCitySelect
            key={addrPickerKey}
            countryValue={formData.country || "USA"}
            stateValue={formData.province || ""}
            cityValue={formData.city || ""}
            onCountryChange={(val) => handleChange("country", val)}
            onStateChange={(val) => handleChange("province", val)}
            onCityChange={(val) => handleChange("city", val)}
            isEditing={true}
            required={true}
            stateLabel="State"
            cityLabel="City"
          />
        </div>
        {bottomFields.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {f.label} {!f.optional && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={formData[f.key]}
              onChange={(e) => handleChange(f.key, e.target.value)}
              placeholder={f.key === "phone" ? "(XXX) XXX-XXXX" : ""}
              maxLength={f.key === "phone" ? 14 : f.key === "postal_code" ? 5 : undefined}
              className={`w-full px-3 py-2 border rounded-lg text-sm ${errors[f.key] ? "border-red-400" : "border-gray-300"} focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
            />
            {errors[f.key] && <p className="text-xs text-red-500 mt-1">{errors[f.key]}</p>}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button onClick={() => handleSubmit(onSaveAndExit)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">
          Save & Exit
        </button>
        <button onClick={() => handleSubmit(onSave)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2">
          {saving && <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

export default PersonalDetailsForm;
